import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faQrcode, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import {
  OWNERSHIP_TYPE_LABELS,
  SHIPMENT_TYPE_LABELS,
  type EquipmentIntake,
  type ReceivedCondition,
} from '@/services/equipment-intake.service';
import {
  INSPECTION_STATUS_LABELS,
  WARRANTY_STATUS_LABELS,
  type InspectionStatus,
  type InspectionWarrantyStatus,
} from '@/services/inspection.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import { AddressCard } from './AddressCard';
import { firstInspection, useIntakeInspection } from '../intake-queries';
import {
  ACCESSORY_STATUS_LABEL,
  ACCESSORY_STATUS_STYLE,
  CONDITION_ITEMS,
} from '../intake-form.constants';

/**
 * The intake read-only workspace.
 *
 * ONE renderer for every intake, whatever created it. There is deliberately no
 * bulk variant and no branch anywhere in this file on how the intake came to
 * exist — a bulk-created intake and a hand-typed one differ only in which
 * values are filled, never in which sections appear.
 *
 * That is why every section renders unconditionally. A section hidden because
 * it happened to be empty is precisely what made bulk intakes look like
 * second-class records: they carry no photos, no shipments and no condition
 * checklist, so half the modal used to disappear. Now the section is always
 * there and says what it does not have.
 *
 * Every section resolves to exactly one of three states:
 *   1. data          — render it
 *   2. no data       — say so, in the section's own words
 *   3. no permission — say so, without revealing whether data exists
 *
 * States 2 and 3 must stay distinguishable. The API encodes them for billing as
 * `undefined` (not authorised) vs `null` (nothing recorded), and collapsing
 * them here would leak the existence of billing data to someone not allowed to
 * see it.
 */

const INSPECTION_STATUS_BADGE: Partial<Record<InspectionStatus, string>> = {
  UNASSIGNED: 'bg-muted text-muted-foreground',
  SCHEDULED: 'bg-blue-500/10 text-blue-600',
  ASSIGNED: 'bg-purple-500/10 text-purple-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
  REVIEWED: 'bg-teal-500/10 text-teal-700',
  CLOSED: 'bg-muted text-muted-foreground',
  CANCELLED: 'bg-red-500/10 text-red-700',
  TRANSFER_REQUESTED: 'bg-amber-500/10 text-amber-700',
};

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

/** The single-missing-value vocabulary: an em dash, never a blank cell. */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card px-4 py-3">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** States 2 and 3 both land here — the copy is what distinguishes them. */
function Note({ children }: { children: React.ReactNode }) {
  return <p className="text-sm italic text-muted-foreground">{children}</p>;
}

function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="presentation"
    >
      <img
        src={url}
        alt="Inward photo"
        className="max-h-full max-w-full rounded-lg object-contain"
      />
    </div>
  );
}

function isReceivedCondition(v: unknown): v is ReceivedCondition {
  if (!v || typeof v !== 'object') return false;
  return CONDITION_ITEMS.some((item) => item.key in (v as Record<string, unknown>));
}

export function IntakeDetails({
  intake,
  onEditAddresses,
}: {
  intake: EquipmentIntake;
  onEditAddresses?: (intake: EquipmentIntake) => void;
}) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const can = useHasPermission();

  const canViewBilling = can('intake:billing:view');
  const canEditAddresses = canViewBilling && Boolean(onEditAddresses);
  // The Workflow section reads the Inspection module, which is gated
  // independently of intake:view. Gate the request too — firing it only to
  // swallow a 403 would be noise in the server logs and in the network tab.
  const canReadInspection = can('inspection:read');

  // Existing endpoint, existing filter. The intake API deliberately does not
  // carry inspection fields — that would duplicate a resource that already has
  // its own endpoint and its own permission.
  const inspectionQuery = useIntakeInspection(intake.id, canReadInspection);
  const inspection = firstInspection(inspectionQuery.data);
  // A 403 despite the client-side check means the server disagrees; it wins.
  const inspectionForbidden =
    axios.isAxiosError(inspectionQuery.error) && inspectionQuery.error.response?.status === 403;

  const accessories = intake.accessories;
  const unknownAccessories = accessories.filter((a) => a.accessoryId === null);
  const receivedCount = accessories.filter((a) => a.status === 'RECEIVED').length;
  const missingCount = accessories.filter((a) => a.status === 'MISSING').length;
  const damagedCount = accessories.filter((a) => a.status === 'DAMAGED').length;

  const condition = isReceivedCondition(intake.receivedConditionJson)
    ? intake.receivedConditionJson
    : null;

  const registered = intake.customerRegisteredAddress;
  const registeredLine = registered
    ? [
        registered.line1,
        registered.line2,
        registered.city,
        registered.state,
        registered.postalCode,
        registered.country,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  const hasContact = Boolean(
    intake.customerContactName || intake.customerContactEmail || intake.customerContactPhone,
  );

  return (
    <>
      <div className="space-y-3">
        {/* ── Header ──────────────────────────────────────────────────────────
            Everything needed to know WHICH machine this is, above the fold:
            customer, equipment, asset, serial, inspection and status. An
            operator holding the machine identifies it here without scrolling
            and without opening Edit. */}
        <header className="rounded-lg border border-border bg-muted/30 px-4 py-3">
          <p className="font-mono text-lg font-semibold text-primary">{intake.intakeNumber}</p>

          <p className="mt-1 text-sm font-medium text-foreground">
            {intake.customerOrganizationName ?? intake.customerName ?? 'Company equipment'}
          </p>
          <p className="text-sm text-muted-foreground">{intake.equipmentName}</p>

          {/* STEP 3 — workflow status comes from the INSPECTION and only from
              there. EquipmentIntake.status is not workflow state: no backend
              code advances it past RECEIVED, so showing it here would present a
              value nothing maintains as if it tracked progress. */}
          <p className="mt-2 flex items-center gap-2 text-xs">
            {!canReadInspection || inspectionForbidden ? (
              <span className="text-muted-foreground">Status unavailable</span>
            ) : inspection ? (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-medium ${INSPECTION_STATUS_BADGE[inspection.status as InspectionStatus] ?? 'bg-muted text-muted-foreground'}`}
              >
                {INSPECTION_STATUS_LABELS[inspection.status as InspectionStatus]}
              </span>
            ) : (
              <span className="text-muted-foreground">No inspection raised</span>
            )}
            <span className="text-muted-foreground">{fmtDate(intake.intakeDate)}</span>
          </p>

          <dl className="mt-2.5 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
            <div className="flex gap-1.5">
              <dt className="text-muted-foreground">Asset:</dt>
              <dd className="font-mono text-foreground">{intake.equipmentAssetNumber || '—'}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted-foreground">Serial:</dt>
              <dd className="font-mono text-foreground">{intake.equipmentSerialNumber || '—'}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted-foreground">Inspection:</dt>
              <dd className="font-mono">
                {!canReadInspection || inspectionForbidden ? (
                  <span className="text-muted-foreground">—</span>
                ) : inspection ? (
                  <Link
                    to={ROUTES.INSPECTION_DETAIL.replace(':id', inspection.id)}
                    className="text-primary hover:underline"
                  >
                    {inspection.inspectionNumber}
                    <FontAwesomeIcon
                      icon={faUpRightFromSquare}
                      className="ml-1 h-2.5 w-2.5 align-baseline"
                    />
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </div>
          </dl>
        </header>

        {/* ── 1. INTAKE ─────────────────────────────────────────────────────── */}
        <Section title="Inward">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field
              label="Inward number"
              value={<span className="font-mono text-primary">{intake.intakeNumber}</span>}
            />
            <Field label="Inward date" value={fmtDate(intake.intakeDate)} />
            {/* Ownership lives here and ONLY here. It is the frozen
                ownership-at-intake snapshot, not the equipment's current
                ownership, so showing it beside the equipment would invite
                reading it as a property of the machine. */}
            <Field label="Ownership" value={OWNERSHIP_TYPE_LABELS[intake.ownershipTypeAtIntake]} />
            <Field label="Received by" value={intake.receivedByName} />
            <Field label="Received at" value={fmtDateTime(intake.receivedAt)} />
          </dl>
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2">
            <FontAwesomeIcon icon={faQrcode} className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Inward code</p>
              <p className="font-mono text-sm font-medium text-foreground">{intake.intakeCode}</p>
              <p className="truncate font-mono text-xs text-muted-foreground">
                {intake.qrCodeValue}
              </p>
            </div>
          </div>
        </Section>

        {/* ── 2. CUSTOMER ───────────────────────────────────────────────────── */}
        <Section title="Customer">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field
              label="Legal organization name"
              value={intake.customerOrganizationName ?? intake.customerName}
            />
            <Field label="Customer code" value={intake.customerCode} />
            {/* This is also the "Received From" location. It is stated once,
                here, and never repeated under Contact. */}
            <Field label="Customer location" value={intake.customerLocationName} />
          </dl>
          <div className="mt-3">
            <p className="text-xs text-muted-foreground">Registered address</p>
            {registeredLine ? (
              <>
                <p className="mt-0.5 text-sm text-foreground">{registeredLine}</p>
                {/* Deliberately plain text, NOT an AddressCard. The card is
                    reserved for frozen Bill-To / Ship-To snapshots; making
                    these look alike is how a live master-data address gets
                    mistaken for the address that was invoiced. */}
                <p className="mt-1 text-xs italic text-muted-foreground">
                  Current customer master data — not the invoiced address, and never carries GST.
                </p>
              </>
            ) : (
              <Note>No registered address recorded for this customer.</Note>
            )}
          </div>
        </Section>

        {/* ── 3. CONTACT ────────────────────────────────────────────────────── */}
        <Section title="Contact">
          {hasContact ? (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Contact person" value={intake.customerContactName} />
              <Field label="Email" value={intake.customerContactEmail} />
              <Field label="Phone" value={intake.customerContactPhone} />
            </dl>
          ) : (
            <Note>No contact assigned.</Note>
          )}
        </Section>

        {/* ── 4. BILLING ────────────────────────────────────────────────────── */}
        <Section title="Billing">
          {intake.billToAddress === undefined ? (
            <Note>You do not have permission to view billing details.</Note>
          ) : intake.billToAddress === null ? (
            <Note>No Bill-To address was captured for this intake.</Note>
          ) : (
            <>
              <AddressCard
                side="Bill-To"
                address={intake.billToAddress}
                canEdit={canEditAddresses}
                onEdit={() => onEditAddresses?.(intake)}
              />
              {/* GST appears here and nowhere else in this component. */}
              <dl className="mt-2">
                <Field label="GST number" value={intake.billToAddress.gstNumber} />
              </dl>
            </>
          )}
        </Section>

        {/* ── 5. SHIPPING ───────────────────────────────────────────────────── */}
        <Section title="Shipping">
          {intake.shipToAddress === undefined ? (
            <Note>You do not have permission to view shipping details.</Note>
          ) : intake.shipToAddress === null ? (
            <Note>No Ship-To address was captured for this intake.</Note>
          ) : (
            <AddressCard
              side="Ship-To"
              address={intake.shipToAddress}
              canEdit={canEditAddresses}
              onEdit={() => onEditAddresses?.(intake)}
            />
          )}
        </Section>

        {/* ── 6. EQUIPMENT ──────────────────────────────────────────────────── */}
        <Section title="Equipment">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Equipment name" value={intake.equipmentName} />
            <Field
              label="Asset number"
              value={<span className="font-mono">{intake.equipmentAssetNumber || '—'}</span>}
            />
            <Field
              label="Serial number"
              value={<span className="font-mono">{intake.equipmentSerialNumber || '—'}</span>}
            />
            <Field label="Model" value={intake.equipmentModelNumber} />
            <Field label="Brand" value={intake.equipmentBrandName} />
            <Field label="Category" value={intake.equipmentCategoryName} />
          </dl>
        </Section>

        {/* ── 7. SERVICE ────────────────────────────────────────────────────── */}
        <Section title="Service">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Complaint description</p>
              <p className="mt-0.5 text-sm text-foreground">{intake.complaintDescription || '—'}</p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground">Condition on receipt</p>
              {/* All six items render whether or not anything was recorded, so
                  the checklist reads the same on every intake. A bulk-created
                  intake shows six unset items and says why — it does not show
                  a shorter list. */}
              <div className="mt-1.5 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {CONDITION_ITEMS.map((item) => {
                  const flag = condition?.[item.key];
                  const present = flag?.present === true;
                  return (
                    <div
                      key={item.key}
                      className="flex items-start gap-2 rounded-lg border border-border px-2.5 py-1.5"
                    >
                      <span
                        className={`mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                          present ? 'bg-amber-500' : 'bg-muted-foreground/30'
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground">{item.label}</p>
                        {flag?.notes && (
                          <p className="text-xs italic text-muted-foreground">{flag.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {condition ? (
                condition.generalRemarks && (
                  <p className="mt-1.5 text-xs text-muted-foreground">{condition.generalRemarks}</p>
                )
              ) : (
                <p className="mt-1.5 text-sm italic text-muted-foreground">
                  No condition checklist was recorded at intake.
                </p>
              )}
              {intake.conditionOnReceipt && (
                <p className="mt-1.5 text-sm text-foreground">{intake.conditionOnReceipt}</p>
              )}
            </div>

            <div>
              <p className="text-xs text-muted-foreground">Remarks</p>
              <p className="mt-0.5 text-sm text-foreground">{intake.remarks || '—'}</p>
            </div>
          </div>
        </Section>

        {/* ── 8. ACCESSORIES ────────────────────────────────────────────────── */}
        <Section title={`Accessories (${accessories.length})`}>
          {accessories.length === 0 ? (
            <Note>No accessories recorded.</Note>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <span className="rounded-full bg-green-500/10 px-2.5 py-0.5 text-green-700">
                  {receivedCount} Received
                </span>
                {missingCount > 0 && (
                  <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-amber-700">
                    {missingCount} Missing
                  </span>
                )}
                {damagedCount > 0 && (
                  <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-red-700">
                    {damagedCount} Damaged
                  </span>
                )}
                {unknownAccessories.length > 0 && (
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-muted-foreground">
                    {unknownAccessories.length} not in catalogue
                  </span>
                )}
              </div>
              <div className="mt-2 space-y-1.5">
                {accessories.map((a) => (
                  <div key={a.id} className="rounded-lg border border-border px-3 py-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-foreground">
                        {a.accessoryName}
                        {/* An accessory the catalogue does not know is still a
                            real item somebody handed over. Marked, never
                            dropped. */}
                        {a.accessoryId === null && (
                          <span className="ml-1.5 text-xs italic text-muted-foreground">
                            (not in catalogue)
                          </span>
                        )}
                      </span>
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${ACCESSORY_STATUS_STYLE[a.status]}`}
                      >
                        {ACCESSORY_STATUS_LABEL[a.status]}
                      </span>
                    </div>
                    {a.notes && <p className="text-xs italic text-muted-foreground">{a.notes}</p>}
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>

        {/* ── 9. PHOTOS ─────────────────────────────────────────────────────── */}
        <Section title="Photos">
          {intake.photoUrls.length === 0 ? (
            <Note>No intake photos uploaded.</Note>
          ) : (
            <div className="flex flex-wrap gap-2">
              {intake.photoUrls.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxUrl(url)}
                  className="h-16 w-16 overflow-hidden rounded-lg border border-border bg-muted/40 transition-colors hover:border-primary"
                  title="View photo"
                >
                  <img
                    src={url}
                    alt={`Intake photo ${i + 1}`}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </Section>

        {/* ── 10. WORKFLOW ──────────────────────────────────────────────────── */}
        <Section title="Workflow">
          {!canReadInspection || inspectionForbidden ? (
            <Note>You do not have permission to view inspection details.</Note>
          ) : inspectionQuery.isLoading ? (
            <Note>Loading inspection details…</Note>
          ) : (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Assigned engineer" value={inspection?.assignedEngineerName} />
              <Field
                label="Inspection number"
                value={
                  inspection ? (
                    <Link
                      to={ROUTES.INSPECTION_DETAIL.replace(':id', inspection.id)}
                      className="font-mono text-primary hover:underline"
                    >
                      {inspection.inspectionNumber}
                      <FontAwesomeIcon
                        icon={faUpRightFromSquare}
                        className="ml-1 h-2.5 w-2.5 align-baseline"
                      />
                    </Link>
                  ) : null
                }
              />
              <Field
                label="Inspection status"
                value={
                  inspection
                    ? INSPECTION_STATUS_LABELS[inspection.status as InspectionStatus]
                    : null
                }
              />
              <Field
                label="Warranty status"
                value={
                  inspection
                    ? WARRANTY_STATUS_LABELS[inspection.warrantyStatus as InspectionWarrantyStatus]
                    : null
                }
              />
              <Field
                label="Receiving lot"
                value={
                  intake.receivingLotId && intake.receivingLotNumber ? (
                    <Link
                      to={ROUTES.OPS_RECEIVING_LOT_DETAIL.replace(':id', intake.receivingLotId)}
                      className="font-mono text-primary hover:underline"
                    >
                      {intake.receivingLotNumber}
                      <FontAwesomeIcon
                        icon={faUpRightFromSquare}
                        className="ml-1 h-2.5 w-2.5 align-baseline"
                      />
                    </Link>
                  ) : null
                }
              />
              <Field label="Batch reference" value={inspection?.batchReferenceNo} />
              {!inspection && (
                <div className="col-span-2 sm:col-span-3">
                  <Note>No inspection has been raised for this intake.</Note>
                </div>
              )}
            </dl>
          )}
        </Section>

        {/* ── 11. AUDIT ─────────────────────────────────────────────────────── */}
        <Section title="Audit">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Received by" value={intake.receivedByName} />
            <Field label="Received at" value={fmtDateTime(intake.receivedAt)} />
            <Field label="Updated at" value={fmtDateTime(intake.updatedAt)} />
          </dl>
        </Section>

        {/* Shipments are not one of the eleven sections, but an intake that has
            them would otherwise lose them entirely on this screen. Rendered
            only when present: unlike the eleven, this is not a field an intake
            is expected to have. */}
        {intake.shipments.length > 0 && (
          <Section title={`Shipments (${intake.shipments.length})`}>
            <div className="space-y-2">
              {intake.shipments.map((s) => (
                <div key={s.id} className="rounded-lg border border-border px-3 py-2">
                  <p className="text-sm text-foreground">
                    {SHIPMENT_TYPE_LABELS[s.shipmentType]}
                    {s.courierName ? ` · ${s.courierName}` : ''}
                  </p>
                  <dl className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Field label="Tracking" value={s.trackingNumber} />
                    <Field label="Dispatched" value={fmtDate(s.dispatchDate)} />
                    <Field label="Received" value={fmtDate(s.receivedDate)} />
                  </dl>
                  {s.remarks && (
                    <p className="mt-1 text-xs italic text-muted-foreground">{s.remarks}</p>
                  )}
                  {/* Proof-of-delivery notes, courier receipts and damage
                      photos. The API has always resolved these into signed
                      URLs — this screen simply never rendered them, so an
                      attachment somebody uploaded was invisible on the only
                      page that reads the intake. */}
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">
                      Attachments ({s.attachments.length})
                    </p>
                    {s.attachments.length === 0 ? (
                      <p className="mt-0.5 text-sm italic text-muted-foreground">
                        No shipment attachments uploaded.
                      </p>
                    ) : (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {s.attachments.map((a, i) => {
                          const isImage = /\.(jpe?g|png|webp|heic)$/i.test(a.key);
                          return isImage ? (
                            <button
                              key={a.key}
                              onClick={() => setLightboxUrl(a.url)}
                              className="h-16 w-16 overflow-hidden rounded-lg border border-border bg-muted/40 transition-colors hover:border-primary"
                              title="View attachment"
                            >
                              <img
                                src={a.url}
                                alt={`Shipment attachment ${i + 1}`}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </button>
                          ) : (
                            <a
                              key={a.key}
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-primary hover:border-primary"
                            >
                              <FontAwesomeIcon icon={faPaperclip} className="h-3 w-3" />
                              Attachment {i + 1}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      {lightboxUrl && <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </>
  );
}
